// qs-useDrawingManagement.ts
import type { Drawing, DrawingCanvasState, DrawingToolState } from '@/stores/qs-store-gallery'
import type { Layer, TextObject } from '@/types/qs-types-index'
import { useCanvasStore } from '@/stores/qs-store-canvas'
import { useGalleryStore } from '@/stores/qs-store-gallery'
import { useLayerStore } from '@/stores/qs-store-layers'
import { useToolStore } from '@/stores/qs-store-tools'
import { nextTick, watch } from 'vue'

export function useDrawingManagement() {
  const layerStore = useLayerStore()
  const canvasStore = useCanvasStore()
  const toolStore = useToolStore()
  const galleryStore = useGalleryStore()

  let autoSaveTimeout: number | null = null
  let watcherStopFunctions: Array<() => void> = []

  const getCanvasState = (): DrawingCanvasState => ({
    scale: canvasStore.scale,
    position: canvasStore.position,
    backgroundColor: canvasStore.backgroundColor
  })

  const getToolState = (): DrawingToolState => ({
    currentTool: toolStore.currentTool,
    backgroundColor: toolStore.backgroundColor,
    colorSwatches: toolStore.colorSwatches,
    brushPresets: toolStore.brushPresets
  })

  const loadCanvasState = (canvasState: DrawingCanvasState | undefined): void => {
    canvasStore.setScale(canvasState?.scale ?? 1)
    canvasStore.setPosition(canvasState?.position ?? { x: 0, y: 0 })
  }

  const loadToolState = (toolState: DrawingToolState | undefined): void => {
    if (toolState?.currentTool) {
      const toolWithDefaults = {
        ...toolState.currentTool,
        brushType: toolState.currentTool.brushType ?? 'round'
      }
      toolStore.setTool(toolWithDefaults)
    }
    if (toolState?.backgroundColor) {
      toolStore.setBackgroundColor(toolState.backgroundColor)
    }
    if (toolState?.colorSwatches) {
      toolStore.colorSwatches = toolState.colorSwatches
    }
    if (toolState?.brushPresets) {
      toolStore.brushPresets = toolState.brushPresets
    }
  }

  const loadTextObjects = async (textObjects: TextObject[] | undefined): Promise<void> => {
    try {
      const { useTextStore } = await import('@/stores/qs-store-text')
      const textStore = useTextStore()

      textStore.clearAllTextObjects()

      if (textObjects && Array.isArray(textObjects)) {
        textObjects.forEach(textObject => {
          textStore.addTextObject(textObject)
        })
      }
    } catch (error) {
      console.error('Failed to load text objects:', error)
    }
  }

  const reinitializeCanvas = (): void => {
    nextTick(() => {
      setTimeout(() => {
        const stage = canvasStore.stage
        if (stage) {
          stage.destroyChildren()
          stage.batchDraw()

          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('force-canvas-reinit'))
          }, 50)
        }
      }, 50)
    })
  }

  let lastSavedState: string | null = null

  const saveCurrentDrawing = async (): Promise<void> => {
    const currentDrawing = galleryStore.currentDrawing
    if (!currentDrawing) return

    let textObjects: TextObject[] = []
    try {
      const { useTextStore } = await import('@/stores/qs-store-text')
      const textStore = useTextStore()
      textObjects = textStore.textObjects ?? []
    } catch (error) {
      console.error('Failed to get text objects for saving:', error)
    }

    const currentState = JSON.stringify({
      layers: layerStore.layers,
      canvasState: getCanvasState(),
      toolState: getToolState(),
      textObjects
    })

    if (currentState === lastSavedState) {
      return
    }

    lastSavedState = currentState

    galleryStore.saveCurrentDrawing(
      layerStore.layers,
      getCanvasState(),
      getToolState(),
      textObjects
    )
  }

  const loadDrawing = (drawing: Drawing | null | undefined): void => {
    if (!drawing) {
      console.warn('DrawingManagement: No drawing provided to load')
      return
    }

    console.log('🎨 Loading drawing:', drawing.name || 'Untitled')

    galleryStore.currentDrawingId = drawing.id

    if (drawing.layers && drawing.layers.length > 0) {
      layerStore.layers = drawing.layers

      const savedLayerId = drawing.currentLayerId ?? drawing.layers[0].id
      const layerExists = drawing.layers.some((layer: Layer) => layer.id === savedLayerId)
      layerStore.currentLayerId = layerExists ? savedLayerId : drawing.layers[0].id
    } else {
      layerStore.initializeDefaultLayers()
    }

    loadCanvasState(drawing.canvasState)
    loadToolState(drawing.toolState)
    loadTextObjects(drawing.textObjects)

    const backgroundColor = drawing.canvasState?.backgroundColor ??
                          drawing.toolState?.backgroundColor ??
                          toolStore.backgroundColor
    canvasStore.setBackgroundColor(backgroundColor)

    reinitializeCanvas()

    console.log('✅ Drawing loaded successfully:', drawing.name || 'Untitled')
    console.log('📊 Layer store sync - layers:', layerStore.layers.length, 'current:', layerStore.currentLayerId)
  }

  const scheduleAutoSave = (): void => {
    if (autoSaveTimeout) {
      clearTimeout(autoSaveTimeout)
    }

    const delay = galleryStore.isSidebarOpen ? 500 : 1000
    autoSaveTimeout = window.setTimeout(() => {
      saveCurrentDrawing()
    }, delay)
  }

  const setupAutoSave = (): void => {
    watcherStopFunctions.forEach(stop => stop())
    watcherStopFunctions = []

    const stop1 = watch(
      [
        () => layerStore.layers.length,
        () => canvasStore.scale,
        () => canvasStore.position,
        () => toolStore.currentTool.brushType,
        () => toolStore.currentTool.size,
        () => toolStore.currentTool.color,
        () => toolStore.backgroundColor
      ],
      () => {
        scheduleAutoSave()
      }
    )
    watcherStopFunctions.push(stop1)

    let lastPathsHash = ''
    let lastLayersLength = layerStore.layers.length
    const stop2 = watch(
      () => layerStore.layers.length,
      (newLength) => {
        if (newLength !== lastLayersLength) {
          lastLayersLength = newLength
          scheduleAutoSave()
          return
        }

        const currentHash = layerStore.layers.map(l => `${l.id}:${l.paths?.length ?? 0}`).join('|')
        if (currentHash !== lastPathsHash) {
          lastPathsHash = currentHash
          scheduleAutoSave()
        }
      }
    )
    watcherStopFunctions.push(stop2)

    const stop3 = watch(() => galleryStore.isSidebarOpen, (isOpen) => {
      if (isOpen) {
        requestAnimationFrame(() => {
          saveCurrentDrawing()
        })
      }
    })
    watcherStopFunctions.push(stop3)

    setTimeout(() => {
      saveCurrentDrawing()
    }, 500)
  }

  const handleDrawingCompleted = (): void => {
    requestAnimationFrame(() => {
      saveCurrentDrawing()
    })
  }

  const handleDrawingSelected = (drawing: Drawing): void => {
    loadDrawing(drawing)
    galleryStore.closeSidebar()
  }

  const closeGallery = (): void => {
    galleryStore.closeSidebar()
  }

  const cleanup = (): void => {
    watcherStopFunctions.forEach(stop => stop())
    watcherStopFunctions = []
    
    if (autoSaveTimeout) {
      clearTimeout(autoSaveTimeout)
      autoSaveTimeout = null
    }
    saveCurrentDrawing()
  }

  return {
    saveCurrentDrawing,
    loadDrawing,
    handleDrawingCompleted,
    handleDrawingSelected,
    closeGallery,
    setupAutoSave,
    cleanup
  }
}